import { FilterMenu } from '@calendly/ui/components/filter-menu';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { mergeRefs } from '@calendly/ui/utils/merge-refs';
import classNames from 'classnames';
import { useCallback, useRef } from 'react';
import { useAppShellFilterMenuScrollLock } from 'features/app_shell';
import { setFilterMenuContainerElement } from 'hooks/filter_menu_container_registry';
import { createShouldDeferFilterMenuEscapeClose } from 'hooks/filter_menu_escape';
import { useCloseMeetingsFilterMenuOnSettingsModal } from 'hooks/use_close_meetings_filter_menu_on_settings_modal';
import { useFilterMenuPortalFocusAllow } from 'hooks/use_filter_menu_portal_focus_allow';
import { getFilterMenuContentElement } from './filter_menu_content_registry';
import { allowFilterMenuCloseWhenCheckboxFocused, finalizeFilterMenuCloseSuppression, shouldSuppressFilterMenuTriggerToggleOpen, useFilterMenuPortalDismissGuard } from './filter_menu_portal_dismiss_guard';
import { ensureFilterMenuPortalRoots, getFilterMenuPortalRoots } from './filter_menu_portals';
import { FilterMenuScrollBehaviorProvider } from './filter_menu_scroll_behavior_context';
const shouldDeferFilterMenuEscapeClose = () => createShouldDeferFilterMenuEscapeClose({
  getPortalRoots: getFilterMenuPortalRoots,
  getMenuContentElement: getFilterMenuContentElement
})();
const MeetingsFilterMenuSettingsModalClose = ({
  isOpen,
  onClose
}) => {
  useCloseMeetingsFilterMenuOnSettingsModal(isOpen, onClose);
  return null;
};
const containerResponsiveCss = "cdirgh0";
const containerPhoneCss = "c1mg50d0";
const containerCompactCss = "c1baihnn";
const containerCss = "c16ezo8m";

/**
 * Shared FilterMenu shell for scheduling and meetings filter bars.
 * Escape is handled in {@link SchedulingFilterMenuContent}.
 */
export const SchedulingFilterMenu = ({
  isOpen,
  children,
  onOpenChange,
  onClose,
  offset = 0,
  responsive = false,
  contentMinWidth: contentMinWidthProp,
  className,
  placement = 'bottom start',
  closeOnSettingsModalOpen = false,
  closeOnPageScroll = true,
  pullLeft,
  ...props
}) => {
  const contentMinWidth = contentMinWidthProp ?? 'default';
  const isSmallPhone = useDeviceType('isSmallPhone');
  const containerRef = useRef(null);
  useFilterMenuPortalDismissGuard(isOpen, {
    closeOnPageScroll
  });
  useFilterMenuPortalFocusAllow(isOpen);
  useAppShellFilterMenuScrollLock(isOpen && !closeOnPageScroll);
  const handleOpenChange = useCallback(nextIsOpen => {
    if (nextIsOpen) {
      if (shouldSuppressFilterMenuTriggerToggleOpen()) {
        return;
      }
      ensureFilterMenuPortalRoots();
      onOpenChange?.(nextIsOpen);
      return;
    }
    if (shouldDeferFilterMenuEscapeClose()) {
      return;
    }
    allowFilterMenuCloseWhenCheckboxFocused();
    finalizeFilterMenuCloseSuppression();
    onOpenChange?.(nextIsOpen);
  }, [onOpenChange]);
  const handleClose = useCallback(() => {
    if (shouldDeferFilterMenuEscapeClose()) {
      return;
    }
    allowFilterMenuCloseWhenCheckboxFocused();
    finalizeFilterMenuCloseSuppression();
    onClose?.();
  }, [onClose]);
  const closeForOverlay = useCallback(() => {
    onOpenChange?.(false);
    onClose?.();
  }, [onClose, onOpenChange]);
  const setContainerRef = useCallback(node => {
    containerRef.current = node;
    setFilterMenuContainerElement(node);
  }, []);
  return <>
      {closeOnSettingsModalOpen ? <MeetingsFilterMenuSettingsModalClose isOpen={isOpen} onClose={closeForOverlay} /> : null}
      <FilterMenuScrollBehaviorProvider closeOnPageScroll={closeOnPageScroll} pullLeft={pullLeft}>
        <FilterMenu ref={mergeRefs(containerRef, setContainerRef)} isOpen={isOpen} onOpenChange={handleOpenChange} onClose={handleClose} placement={placement} offset={offset} contentMinWidth={contentMinWidth} responsive={responsive} {...props} className={classNames(containerCss, {
        [containerResponsiveCss]: responsive,
        [containerPhoneCss]: isSmallPhone,
        [containerCompactCss]: contentMinWidth !== 'default'
      }, className)}>
          {children}
        </FilterMenu>
      </FilterMenuScrollBehaviorProvider>
    </>;
};

/** Fixed filter bar: keep the panel open and follow the trigger while the page scrolls. */
export const MeetingsFilterMenu = ({
  pullLeft = true,
  ...props
}) => <SchedulingFilterMenu {...props} pullLeft={pullLeft} closeOnPageScroll={false} closeOnSettingsModalOpen />;

require("./filter_menu.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./filter_menu.js");